import { createAsyncThunk } from '@reduxjs/toolkit';
import * as R from 'ramda';

import { AddToBasketModal } from 'src/featureFlags/featureFlags';
import { navigateToBasket } from 'src/redux/productInfo/thunks';
import type { ReduxApplicationState } from 'src/redux/types';
import * as basketAPIMutations from 'src/services/basket-api/mutations';
import * as basketAPIQueries from 'src/services/basket-api/queries';
import type {
  BasketAPIBasketQuery,
  BasketAPIBasketWithItemsQuery,
  BasketAPIMutationTrackingData,
} from 'src/services/basket-api/types/BasketAPIResponseBody';
import type { ProductOptionInputWithoutLabel } from 'src/services/basket-api/types/graphql';
import type { CatServiceAPIProductOptionValue } from 'src/services/cat-service-api/types/CatServiceAPIProductOption';
import type { SelectedPersonalisationsRecord } from '../personalisation/types';
import { actions as ProductInfoActions } from '../productInfo/slice';
import { dispatchBasketUpdatedEvent } from './domEvents';

export const getBasketForViewing = createAsyncThunk<BasketAPIBasketQuery>(
  'basket/getBasketForViewing',
  async () => await basketAPIQueries.getBasket(),
);

export const getBasketForEditing = createAsyncThunk<BasketAPIBasketWithItemsQuery>(
  'basket/getBasketForEditing',
  async () => await basketAPIQueries.getBasketWithItemOptions(),
);

export const mapProductOptionsToBasketOptions = (
  selectedPersonalisations: SelectedPersonalisationsRecord,
): ProductOptionInputWithoutLabel =>
  R.chain(
    (optionId) =>
      /* istanbul ignore next */
      selectedPersonalisations?.[optionId]?.map(
        ({ id, isCustomInput, name }: CatServiceAPIProductOptionValue) =>
          isCustomInput ? { optionId, value: name } : { optionId, valueId: String(id) },
      ) ?? [],
    Object.keys(selectedPersonalisations),
  );

interface AddItemToOrUpdateItemInBasketPayload {
  basket: BasketAPIBasketWithItemsQuery;
  trackingData: BasketAPIMutationTrackingData;
}

export const addItemToBasket = createAsyncThunk<AddItemToOrUpdateItemInBasketPayload>(
  'basket/addItemToBasket',
  async (_, { dispatch, getState }) => {
    const {
      personalisation: { quantity, selectedPersonalisations },
      product: { code },
      user: { currencyCode, featureFlags = {} },
    } = getState() as ReduxApplicationState;
    const options = mapProductOptionsToBasketOptions(selectedPersonalisations);

    const { basket, trackingData } = await basketAPIMutations.addItem(
      code,
      quantity,
      currencyCode,
      options,
    );

    dispatchBasketUpdatedEvent();

    const addToBasketModalFeature = featureFlags[AddToBasketModal.NAME];

    if (addToBasketModalFeature === AddToBasketModal.Basic) {
      dispatch(ProductInfoActions.openBasketModal());
    } else {
      dispatch(navigateToBasket());
    }

    return {
      basket,
      trackingData,
    };
  },
);

export const updateItemInBasket = createAsyncThunk<AddItemToOrUpdateItemInBasketPayload>(
  'basket/updateItemInBasket',
  async (_, { dispatch, getState }) => {
    const {
      personalisation: { editedItem, quantity, selectedPersonalisations },
      user: { currencyCode },
    } = getState() as ReduxApplicationState;
    const options = mapProductOptionsToBasketOptions(selectedPersonalisations);

    const { basket, trackingData } = await basketAPIMutations.updateItem(
      editedItem!,
      quantity,
      currencyCode,
      options,
    );

    dispatch(navigateToBasket());

    return {
      basket,
      trackingData,
    };
  },
);
